import React, { useState, useEffect, useRef } from 'react';
import { Tabs, Empty, message } from 'antd';
import {
  FileTextOutlined,
  ToolOutlined,
  DatabaseOutlined,
  ImportOutlined,
  ExportOutlined,
  MessageOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { KrybotService } from '../../../services/api/krybotService';
import type { KrybotAgent } from '../../../types/krybot';
import { AgentDetailsTab } from './AgentDetailsTab';
import { AgentCapabilitiesTab } from './AgentCapabilitiesTab';
import { AgentSkillsResourcesTab } from './AgentSkillsResourcesTab';
import { AgentContextTab } from './AgentContextTab';
import { AgentInputTab } from './AgentInputTab';
import { AgentOutputTab } from './AgentOutputTab';
import { AgentPromptBuilderTab } from './AgentPromptBuilderTab';
import styles from './AgentManagement.module.css';

type AgentTabKey =
  | 'details'
  | 'capabilities'
  | 'skills'
  | 'context'
  | 'input'
  | 'output'
  | 'prompt-builder';

interface AgentDetailsPanelProps {
  agentId: string | null;
  isCreating: boolean;
  onSave: () => void;
  onCancel: () => void;
  onAgentChange: (agent: KrybotAgent | null) => void;
}

export const AgentDetailsPanel: React.FC<AgentDetailsPanelProps> = ({
  agentId,
  isCreating,
  onSave,
  onCancel,
  onAgentChange,
}) => {
  const [agent, setAgent] = useState<KrybotAgent | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<AgentTabKey>('details');
  const [agentData, setAgentData] = useState<Partial<KrybotAgent>>({});
  const agentDataRef = useRef<Partial<KrybotAgent>>(agentData);
  agentDataRef.current = agentData;

  useEffect(() => {
    if (agentId && !isCreating) {
      loadAgent();
    } else if (isCreating) {
      setAgent(null);
      // Initialize with default values for new agent
      setAgentData({
        is_active: true,
        capabilities: {},
        default_context: {},
        input_schema: {},
        output_schema: {},
      });
    } else {
      setAgent(null);
      setAgentData({});
    }
  }, [agentId, isCreating]);

  const loadAgent = async () => {
    if (!agentId) return;
    try {
      setLoading(true);
      const loadedAgent = await KrybotService.getAgent(agentId);
      setAgent(loadedAgent);
      setAgentData(loadedAgent);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to load agent';
      message.error(`Failed to load agent: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAgentDataChange = (updates: Partial<KrybotAgent>) => {
    const updated = { ...agentDataRef.current, ...updates };
    agentDataRef.current = updated;
    setAgentData(updated);
    if (agent) {
      const updatedAgent = { ...agent, ...updates };
      setAgent(updatedAgent);
      onAgentChange(updatedAgent);
    }
  };

  const handleSave = async () => {
    const payload = agentDataRef.current;
    try {
      if (isCreating) {
        const newAgent = await KrybotService.createAgent(payload as any);
        message.success('Agent created successfully');
        onAgentChange(newAgent);
        onSave();
      } else if (agent?.id) {
        await KrybotService.updateAgent(agent.id, payload as any);
        message.success('Agent updated successfully');
        await loadAgent();
        onSave();
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to save agent';
      message.error(`Failed to save agent: ${errorMsg}`);
    }
  };

  if (!agentId && !isCreating) {
    return (
      <div className="agent-details-panel-empty">
        <Empty
          description="Select an agent from the list to view details"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  const currentAgent = agent || (isCreating ? null : agent);

  return (
    <div className="agent-details-panel">
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as AgentTabKey)}
        items={[
          {
            key: 'details',
            label: (
              <span>
                <FileTextOutlined />
                Details
              </span>
            ),
            children: (
              <AgentDetailsTab
                agent={currentAgent}
                agentData={agentData}
                isCreating={isCreating}
                onDataChange={handleAgentDataChange}
                onSave={handleSave}
                onCancel={onCancel}
                loading={loading}
              />
            ),
          },
          {
            key: 'capabilities',
            label: (
              <span>
                <ToolOutlined />
                Capabilities
              </span>
            ),
            children: (
              <AgentCapabilitiesTab
                agent={currentAgent}
                agentData={agentData}
                isCreating={isCreating}
                onDataChange={handleAgentDataChange}
                onSave={handleSave}
                loading={loading}
              />
            ),
          },
          {
            key: 'skills',
            label: (
              <span>
                <BookOutlined />
                Skills &amp; resources
              </span>
            ),
            children: (
              <AgentSkillsResourcesTab
                agent={currentAgent}
                agentData={agentData}
                isCreating={isCreating}
                onDataChange={handleAgentDataChange}
                onSave={handleSave}
                loading={loading}
              />
            ),
          },
          {
            key: 'context',
            label: (
              <span>
                <DatabaseOutlined />
                Context
              </span>
            ),
            children: (
              <AgentContextTab
                agent={currentAgent}
                agentData={agentData}
                isCreating={isCreating}
                onDataChange={handleAgentDataChange}
                onSave={handleSave}
                loading={loading}
              />
            ),
          },
          {
            key: 'input',
            label: (
              <span>
                <ImportOutlined />
                Input
              </span>
            ),
            children: (
              <AgentInputTab
                agent={currentAgent}
                agentData={agentData}
                isCreating={isCreating}
                onDataChange={handleAgentDataChange}
                onSave={handleSave}
                loading={loading}
              />
            ),
          },
          {
            key: 'output',
            label: (
              <span>
                <ExportOutlined />
                Output
              </span>
            ),
            children: (
              <AgentOutputTab
                agent={currentAgent}
                agentData={agentData}
                isCreating={isCreating}
                onDataChange={handleAgentDataChange}
                onSave={handleSave}
                loading={loading}
              />
            ),
          },
          {
            key: 'prompt-builder',
            label: (
              <span>
                <MessageOutlined />
                Prompt Builder
              </span>
            ),
            children: (
              <AgentPromptBuilderTab
                agent={currentAgent}
                agentData={agentData}
                isCreating={isCreating}
                onDataChange={handleAgentDataChange}
                onSave={handleSave}
                loading={loading}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

